import type { RecentConversationEntry } from '@/types';
import { format } from 'date-fns';
import { RecentConversationIcon } from '@/components/icons';

interface RecentConversationsProps {
  entries: RecentConversationEntry[];
  onSelect: (id: string) => void;
  isLoading?: boolean;
  error?: Error | null;
  onRetry?: () => void;
}

export function RecentConversations({
  entries,
  onSelect,
  isLoading,
  error,
  onRetry,
}: RecentConversationsProps) {
  if (isLoading) {
    return (
      <div className="section-panel">
        <h3 className="section-panel__title">Recent conversations</h3>
        <p className="empty-state__desc">Loading conversations…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="section-panel">
        <h3 className="section-panel__title">Recent conversations</h3>
        <p className="empty-state__desc" role="alert">
          Could not load conversations.
        </p>
        {onRetry ? (
          <button type="button" className="recent-convo-card" onClick={() => void onRetry()}>
            Retry
          </button>
        ) : null}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="section-panel">
        <h3 className="section-panel__title">Recent conversations</h3>
        <div className="empty-state">
          <div className="empty-state__icon-circle" aria-hidden="true">
            <RecentConversationIcon size={24} />
          </div>
          <p className="empty-state__title">Your conversations will appear here</p>
          <p className="empty-state__desc">
            When you start a chat, your recent conversations will be saved here so you can quickly
            pick up where you left off.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="section-panel">
      <h3 className="section-panel__title">Recent conversations</h3>
      <div className="recent-convos__list">
        {entries.map((entry) => (
          <button key={entry.id} className="recent-convo-card" onClick={() => onSelect(entry.id)}>
            <RecentConversationIcon size={25} className="recent-convo-card__icon" />
            <div className="recent-convo-card__body">
              <span className="recent-convo-card__title">{entry.title}</span>
              <span className="recent-convo-card__time">
                {format(new Date(entry.timestamp), 'dd/MM/yyyy, h:mm:ss aa')}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
